import * as React from 'react';
import { LoaderCircle, ThumbsUp, ThumbsDown } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import type { ReactionVoter } from '~/modules/build-hub/data-mappers/map-inspiration-reactions';
import { BuildHubRoleEnum } from '../../../../../../generated/graphql';

type VoteType = 'upvote' | 'downvote';

interface VoterPreviewProps {
    /** Array of voters to display */
    voters: ReactionVoter[];
    /** Type of vote (upvote or downvote) */
    voteType: VoteType;
    /** Loading state while fetching data */
    isLoading?: boolean;
}

/**
 * Sorts voters by role: Admin (owner) first, then Editor, then Viewer.
 * Within the same role, maintains original order.
 */
const sortVotersByRole = (voters: ReactionVoter[]): ReactionVoter[] => {
    const getRolePriority = (role: BuildHubRoleEnum): number => {
        switch (role) {
            case BuildHubRoleEnum.Admin:
                return 0;
            case BuildHubRoleEnum.Editor:
                return 1;
            case BuildHubRoleEnum.Viewer:
                return 2;
            default:
                return 3;
        }
    };

    return [...voters].sort((a, b) => {
        return getRolePriority(a.role) - getRolePriority(b.role);
    });
};

/**
 * Displays a simple preview of voters in a hover popover.
 *
 * Features:
 * - Icon (ThumbsUp for upvotes, ThumbsDown for downvotes)
 * - Descriptive label (e.g., "Liked by:", "Disliked by:")
 * - Comma-separated list of voter names
 * - Sorted by role (Admin/Owner > Editor > Viewer)
 * - Fallback to email if name not available
 * - Simple text display with card styling
 * - No click interaction
 *
 * @example
 * ```tsx
 * <VoterPreview
 *   voters={[...]}
 *   voteType="upvote"
 *   isLoading={false}
 * />
 * ```
 */
export function VoterPreview({
    voters,
    voteType,
    isLoading,
}: VoterPreviewProps) {
    if (isLoading) {
        return (
            <div className="flex min-w-[280px] items-center justify-center py-8">
                <LoaderCircle className="size-5 animate-spin text-gray-400" />
            </div>
        );
    }

    if (voters.length === 0) {
        return (
            <div className="min-w-[280px] py-4 text-center text-sm text-gray-500">
                No voters yet
            </div>
        );
    }

    // Sort voters by role
    const sortedVoters = sortVotersByRole(voters);

    // Get display names (fallback to email if name not available)
    const voterNames = sortedVoters.map(
        (voter) => voter.name || voter.email || 'Deleted user'
    );

    const Icon = voteType === 'upvote' ? ThumbsUp : ThumbsDown;
    const label = voteType === 'upvote' ? 'Liked by:' : 'Disliked by:';
    const iconColor = voteType === 'upvote' ? 'text-[#7BD971]' : 'text-red-600';

    return (
        <div
            className={cn(
                'min-w-[280px] max-w-[320px] rounded-xl border border-gray-200 bg-white p-4 shadow-md'
            )}
        >
            <div className="flex items-center gap-2">
                <Icon
                    size={16}
                    className={cn(
                        'h-4 w-4 flex-shrink-0 fill-current',
                        iconColor
                    )}
                />
                <div className="text-sm text-gray-700">
                    <span className="font-medium text-gray-900">{label}</span>{' '}
                    {voterNames.join(', ')}
                </div>
            </div>
        </div>
    );
}
